/**
 * LandingEditorialRoute - Editorial Authority landing (`/` EN + `/vi/` VN).
 * Spec: docs/superpowers/specs/2026-06-11-asklaw-editorial-authority-design-system.md.
 * DECISIONS: s2026-06-11-EDITORIAL-AUTHORITY-LANDING-EN-FIRST.
 * Lang TU PROP (URL-derived) - KHONG useLang detect cho copy (hydration #418 guard).
 * useLang().setLang chi de SYNC context (header/footer route khac) SAU mount.
 * FX: chi Reveal fade-rise + count-up + scroll-progress. KHONG blob/halo/tilt/
 * word-stagger/shimmer (spec sec 12.1 fx inventory).
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import { useLang, type Lang } from '../use-lang';
import { Reveal } from '../lib/reveal';
import { getLandingEditorialCopy } from '../i18n/landing-editorial';
import { HeroCinematic } from '../components/landing/HeroCinematic';
import { KinesisOverlay } from '../components/landing/KinesisOverlay';
import { ArrowIcon } from '../components/landing/ArrowIcon';
import { StatBand } from '../components/landing/StatBand';
import { HowItWorks, TrustTierBlock, AudienceGrid } from '../components/landing/Sections';
import { FooterAnchor } from '../components/landing/FooterAnchor';
import { TrustStrip } from '../components/trust-strip';
import '../styles/landing-editorial.css';

const ORIGIN = 'https://asklaw.vn';

export function LandingEditorialRoute({ lang }: { lang: Lang }) {
  const copy = getLandingEditorialCopy(lang);
  const { lang: ctxLang, setLangEphemeral } = useLang();

  // Route-driven sync: URL (`/` EN, `/vi/` VN) quyet dinh lang khi dang o
  // landing. PHAI ephemeral (khong ghi localStorage) - setLang persist o day
  // tung poison override: visitor VN vao `/` bi luu 'en' vinh vien tren moi
  // product surface (DECISIONS s2026-06-12-GEO-DEFAULT-LANG root cause 2).
  // Dep ctxLang: re-assert neu geo/storage sync flip context giua chung.
  useEffect(() => {
    if (ctxLang !== lang) setLangEphemeral(lang);
  }, [lang, ctxLang, setLangEphemeral]);

  const canonical = lang === 'vi' ? `${ORIGIN}/vi/` : `${ORIGIN}/`;

  return (
    <>
      <Head>
        <html lang={lang} />
        <title>{copy.meta_title}</title>
        <meta name="description" content={copy.meta_description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={`${ORIGIN}/`} />
        <link rel="alternate" hrefLang="vi" href={`${ORIGIN}/vi/`} />
        <link rel="alternate" hrefLang="x-default" href={`${ORIGIN}/`} />
        <meta property="og:locale" content={lang === 'vi' ? 'vi_VN' : 'en_US'} />
        <meta property="og:title" content={copy.meta_title} />
        <meta property="og:description" content={copy.meta_description} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Asklaw',
            url: canonical,
            inLanguage: lang === 'vi' ? 'vi-VN' : 'en',
          })}
        </script>
      </Head>
      {/* .ed-main: giai phong khoi global `main { max-width: 1232px }` de hero +
          section tint band tran full-bleed viewport (spec S2 + DECISIONS
          s2026-06-12-LANDING-HERO-FULL-BLEED-VIEWPORT). */}
      <main id="main" className="ed-main">
        <div id="scroll-progress" className="scroll-progress" aria-hidden="true" role="presentation" />

        <HeroCinematic
          posterSrc="/media/hero-poster.avif"
          posterAlt=""
          videoSources={[
            { src: '/media/hero-loop.h265.mp4', type: 'video/mp4; codecs=hvc1' },
            { src: '/media/hero-loop.h264.mp4', type: 'video/mp4' },
          ]}
        >
          <KinesisOverlay corner="tl" />
          <KinesisOverlay corner="br" />
          <p className="ed-eyebrow">{copy.hero_eyebrow}</p>
          <h1 id="hero-heading" className="ed-display-xl">{copy.hero_headline}</h1>
          <p className="ed-lead">{copy.hero_lead}</p>
          <div className="ed-hero__cta-row">
            <Link to="/chat?from=landing-editorial" className="ed-btn-primary">
              {copy.hero_cta_primary} <ArrowIcon />
            </Link>
            <Link to="/tim-kiem" className="ed-link-arrow">
              {copy.hero_cta_secondary} <ArrowIcon />
            </Link>
          </div>
        </HeroCinematic>

        <Reveal
          as="section"
          className="ed-section ed-section--cream"
          aria-label={lang === 'vi' ? 'Thống kê kho văn bản' : 'Corpus stats'}
        >
          <div className="ed-container">
            <StatBand stats={copy.stats} note={copy.stats_note} />
          </div>
        </Reveal>

        <Reveal as="div"><HowItWorks copy={copy} /></Reveal>

        <Reveal as="section" className="ed-section ed-section--paper" aria-labelledby="features-heading">
          <div className="ed-container">
            <p className="ed-eyebrow">{copy.features_eyebrow}</p>
            <h2 id="features-heading" className="ed-display-lg">{copy.features_heading}</h2>
            <div className="ed-grid-5" style={{ marginTop: 'var(--space-6)' }}>
              {copy.features.map((f) => (
                <Link to={f.to} className="ed-card" key={f.title}>
                  <h3 className="ed-display-md">{f.title}</h3>
                  <hr className="ed-card-rule" />
                  <p style={{ flex: 1 }}>{f.body}</p>
                  <span className="ed-link-arrow">{f.cta} <ArrowIcon /></span>
                </Link>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal as="div"><AudienceGrid copy={copy} /></Reveal>

        <Reveal
          as="section"
          className="ed-section ed-section--paper"
          aria-label={lang === 'vi' ? 'Cam kết minh bạch' : 'Trust signals'}
        >
          <div className="ed-container"><TrustStrip lang={lang} /></div>
        </Reveal>

        <Reveal as="div"><TrustTierBlock copy={copy} /></Reveal>

        <Reveal
          as="section"
          className="ed-section ed-section--cool"
          aria-label={lang === 'vi' ? 'Gói sử dụng' : 'Pricing'}
        >
          <div className="ed-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-5)', flexWrap: 'wrap' }}>
            <h2 className="ed-display-md" style={{ maxWidth: 640 }}>{copy.pricing_heading}</h2>
            <Link to="/chat" className="ed-btn-primary">{copy.pricing_cta} <ArrowIcon /></Link>
          </div>
        </Reveal>

        <FooterAnchor lang={lang} copy={copy} />
      </main>
    </>
  );
}
